/**
 * Keeps the reward bar in sync with the cart, and owns the auto-added gift line.
 *
 * Aurora re-renders the cart drawer / cart page section on the server after every
 * cart mutation and emits 'update-nodes' with the fresh markup, so all the tier
 * math stays in Liquid (snippets/reward-bar.liquid) and this only swaps content.
 *
 * The subscription MUST be synchronous: the drawer calls mount() — which detaches
 * and re-attaches our children — immediately before emitting 'update-nodes' in the
 * same task, so an awaited subscribe would miss that update. This also means this
 * script has to run after bundle.js, so the host element is already upgraded.
 */

const GIFT_PROPERTY = '_reward_gift';

class RewardBarBlock extends HTMLElement {
  /* Shared across instances: one cart, one sync at a time. */
  static isSyncing = false;
  static unavailableGifts = new Set();

  connectedCallback() {
    this.host = this.closest('cart-drawer, cart-page');
    this.host?.on?.('update-nodes', this.handleCartUpdate);
    this.syncGift();
  }

  disconnectedCallback() {
    this.host?.off?.('update-nodes', this.handleCartUpdate);
  }

  handleCartUpdate = ({ node }) => {
    const fresh = node?.querySelector?.('reward-bar-block');

    if (fresh) {
      this.replaceChildren(...fresh.cloneNode(true).childNodes);
    }

    this.syncGift();
  };

  /**
   * Adds the gift for the highest unlocked gift tier and removes any gift line
   * that no longer applies. Only one gift is ever in the cart, because only one
   * "Buy X get Y" discount will actually zero it out at checkout.
   */
  async syncGift() {
    const bar = this.querySelector('[data-reward-bar]');

    if (!bar || RewardBarBlock.isSyncing) return;

    const tiers = this.readJSON(bar.dataset.giftTiers);
    const giftLines = this.readJSON(bar.dataset.giftLines);
    const goalTotal = Number(bar.dataset.goalTotal) || 0;

    const unlocked = tiers.filter(tier => goalTotal >= tier.threshold).pop();
    const wanted =
      unlocked && !RewardBarBlock.unavailableGifts.has(unlocked.variant)
        ? unlocked.variant
        : null;

    const staleLines = giftLines.filter(line => line.variant !== wanted);
    const isMissing = wanted !== null && !giftLines.some(line => line.variant === wanted);

    if (!staleLines.length && !isMissing) return;

    RewardBarBlock.isSyncing = true;

    try {
      let cart;

      for (const line of staleLines) {
        cart = await this.cartRequest(window.routes.cart_change_url, {
          id: line.key,
          quantity: 0,
        });
      }

      if (isMissing) {
        cart = await this.cartRequest(window.routes.cart_add_url, {
          items: [{ id: wanted, quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }],
        });
      }

      this.render(cart);
    } catch (error) {
      /* Gift sold out or otherwise unaddable — stop retrying on every cart change. */
      if (isMissing) {
        RewardBarBlock.unavailableGifts.add(wanted);
      }
      console.warn('reward bar: gift sync failed', error);
    } finally {
      RewardBarBlock.isSyncing = false;
    }
  }

  async cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...body,
        sections: this.host?.dataset.sectionId,
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || response.statusText);
    }

    return data;
  }

  /** Hands the freshly rendered section to the theme so every cart block updates. */
  render(cart) {
    const sectionId = this.host?.dataset.sectionId;
    const html = cart?.sections?.[sectionId];

    if (!html) return;

    /* The theme owns the empty-cart state, so let it re-render that itself. */
    if (cart.item_count === 0) {
      window.location.reload();
      return;
    }

    const document_ = new DOMParser().parseFromString(html, 'text/html');
    const template = document_.querySelector(`[data-sidebar-template="${this.host.id}"]`);
    const node = template ? template.content.firstElementChild : document_;

    this.host.emit?.('update-nodes', { node });
  }

  readJSON(value) {
    try {
      return JSON.parse(value || '[]');
    } catch {
      return [];
    }
  }
}

customElements.define('reward-bar-block', RewardBarBlock);
